import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, CheckCircle2, XCircle, Loader2, LogIn, UserPlus } from 'lucide-react';
import { Button } from 'components/ui/button';
import { CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { AuthLayout, AuthCard } from 'components/auth';
import * as TeamsModule from 'modules/teams';
import * as AuthModule from 'modules/auth';
import config from 'config';

// Survives the signup wizard (incl. an OTP page reload that drops router state)
// so a brand-new student still lands as a candidate, not a new-org owner.
const PENDING_TEAM_JOIN_KEY = 'pending_team_join_token';

interface ValidatedInfo {
  teamName?: string;
  organizationName?: string;
}

// Public landing for a team join link (/join/team/:token). Validates the token,
// shows the class + org, and either joins (authenticated) or routes to
// signup/login carrying the token so onboarding lands the user in the team.
const TeamJoinPage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const {
    state: { isAuthenticated, isFetched, user },
    methods,
  } = AuthModule.Context.useAuthContext();
  // Teachers and students are separate accounts: a STAFF account can never join a
  // class. Surface the "use a student account" guidance instead of a Join button.
  const isStaffAccount = user?.accountType === 'STAFF';

  const [info, setInfo] = useState<ValidatedInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [joinedTeam, setJoinedTeam] = useState<{ name: string; already: boolean } | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);

  const joinByToken = TeamsModule.Hooks.useJoinByToken();

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    TeamsModule.Api.validateJoinToken(token)
      .then(({ data }) => {
        if (data.data?.valid) {
          setInfo({ teamName: data.data.teamName, organizationName: data.data.organizationName });
        } else {
          setInvalid(true);
        }
      })
      .catch(() => setInvalid(true))
      .finally(() => setLoading(false));
  }, [token]);

  const handleJoin = async () => {
    if (!token) return;
    setJoinError(null);
    try {
      const result = await joinByToken.mutateAsync(token);
      setJoinedTeam({ name: result.teamName, already: result.alreadyMember });
      // Make the joined org active, then hard-reload so the profile re-fetches
      // under it and the experience guard routes to the right shell (the portal
      // for students). A plain navigate would keep the stale active org.
      localStorage.setItem(config.api.active_org_key, result.organizationId);
      setTimeout(() => {
        window.location.href = '/';
      }, 1200);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { code?: string; message?: string } } };
      const code = e?.response?.data?.code;
      setJoinError(
        code === 'ORG_FULL'
          ? t('teams.join_page_full')
          : code === 'STAFF_CANNOT_JOIN_CLASS'
            ? t('auth.staff_cannot_join_desc')
            : e?.response?.data?.message || t('teams.join_page_invalid'),
      );
    }
  };

  const goToSignup = () => {
    // Stash a durable copy so the token survives even if the signup wizard loses
    // router state (e.g. an OTP page reload). Signup reads this as a fallback.
    if (token) sessionStorage.setItem(PENDING_TEAM_JOIN_KEY, token);
    navigate('/signup', {
      state: { from: { pathname: `/join/team/${token}` }, teamJoinToken: token },
    });
  };

  const goToLogin = () => {
    // Stash so the join still completes after login even via an existing-account
    // path (e.g. Google) that doesn't return to this page — useProfile applies it.
    if (token) sessionStorage.setItem(PENDING_TEAM_JOIN_KEY, token);
    navigate('/login', { state: { from: { pathname: `/join/team/${token}` } } });
  };

  if (loading) {
    return (
      <AuthLayout>
        <AuthCard>
          <CardContent className="flex flex-col items-center gap-4 py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
            <p className="text-sm text-muted-foreground">{t('teams.join_loading')}</p>
          </CardContent>
        </AuthCard>
      </AuthLayout>
    );
  }

  if (invalid) {
    return (
      <AuthLayout>
        <AuthCard>
          <CardHeader className="items-center text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <XCircle className="h-6 w-6" />
            </div>
            <CardTitle>{t('teams.join_page_invalid')}</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <Button asChild variant="outline" className="w-full" size="lg">
              <Link to={isAuthenticated ? '/app' : '/login'}>
                {isAuthenticated ? t('go_to_dashboard') : t('sign_in')}
              </Link>
            </Button>
          </CardContent>
        </AuthCard>
      </AuthLayout>
    );
  }

  if (joinedTeam) {
    return (
      <AuthLayout>
        <AuthCard>
          <CardHeader className="items-center text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-success/10 text-success">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <CardTitle>{t('welcome')}</CardTitle>
            <CardDescription>
              {joinedTeam.already
                ? t('teams.join_page_already', { team: joinedTeam.name })
                : t('teams.join_page_joined', { team: joinedTeam.name })}
            </CardDescription>
          </CardHeader>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthCard>
        <CardHeader className="items-center text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-foreground">
            <Users className="h-6 w-6" aria-hidden />
          </div>
          <CardTitle className="text-2xl font-bold">
            {t('teams.join_page_heading', { team: info?.teamName })}
          </CardTitle>
          <CardDescription className="mt-2 space-y-2">
            {info?.organizationName && (
              <span className="block font-medium text-foreground">
                {t('teams.join_page_at_org', { org: info.organizationName })}
              </span>
            )}
            <span className="block">{t('teams.join_page_intro')}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {joinError && (
            <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-center text-sm text-destructive">
              {joinError}
            </div>
          )}
          {isAuthenticated && isFetched && isStaffAccount ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-amber-300/40 bg-amber-50/60 px-4 py-3 text-sm dark:border-amber-500/20 dark:bg-amber-500/10">
                <p className="font-semibold text-foreground">{t('auth.staff_cannot_join_title')}</p>
                <p className="mt-0.5 text-muted-foreground">{t('auth.staff_cannot_join_desc')}</p>
              </div>
              <Button onClick={() => methods.logout()} className="w-full" size="lg">
                <LogIn className="mr-2 h-4 w-4" />
                {t('auth.sign_out')}
              </Button>
              <Button asChild variant="outline" className="w-full" size="lg">
                <Link to="/app">{t('go_to_dashboard')}</Link>
              </Button>
            </div>
          ) : isAuthenticated && isFetched ? (
            <Button onClick={handleJoin} disabled={joinByToken.isPending} className="w-full" size="lg">
              {joinByToken.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('teams.join_page_joining')}
                </>
              ) : (
                <>
                  <Users className="mr-2 h-4 w-4" />
                  {t('teams.join_page_cta_join')}
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-3">
              <Button onClick={goToSignup} className="w-full" size="lg">
                <UserPlus className="mr-2 h-4 w-4" />
                {t('teams.join_page_cta_signup')}
              </Button>
              <Button onClick={goToLogin} variant="outline" className="w-full" size="lg">
                <LogIn className="mr-2 h-4 w-4" />
                {t('teams.join_page_cta_login')}
              </Button>
            </div>
          )}
        </CardContent>
      </AuthCard>
    </AuthLayout>
  );
};

export default TeamJoinPage;
